import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Copy, Download, Eye, EyeOff, FolderOpen, ImageDown, Pencil, Plus, Printer, RefreshCcw, RotateCcw, Search, Settings2, Share2, ShoppingCart, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah, ruleCommissions } from "../lib/api";
import { paySaleWithMidtrans } from "../lib/midtrans";
import { useAuth } from "../context/AuthContext";
import { useRouters } from "../context/RouterContext";
import { Button } from "../components/ui/button";
import { RouterCombobox } from "../components/RouterCombobox";
import { FibiLoading } from "../components/FibiLoading";
import { SisaVoucher } from "../components/SisaVoucher";
import { RekonsiliasiLogButton } from "../components/RekonsiliasiLogDialog";
import { HpCustomerPicker } from "../components/HpCustomerPicker";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import CommissionPicker from "../components/CommissionPicker";
import VoucherPrint from "../components/VoucherPrint";
import PinConfirm from "../components/PinConfirm";
import { downloadVoucherPdf } from "../lib/voucherPdf";

const GEN_DEFAULT = { count: 5, length: 6, chars: "mix", profile: "" };
const HS_PROFILE_EMPTY = { name: "", rate_limit: "", validity: "", time_limit: "", data_limit: "", modal: 0, jual: 0, lock_user: "Enable", share_user: 0, codes_per_set: 1, show_price: true, hide_from_agents: false };
const rp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")},-`;
const num = (n) => Number(n || 0).toLocaleString("id-ID");
const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtCreated = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}-${BULAN[d.getMonth()]}-${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const parseSeragam = (p) => {
  const m = /^(\d+)HP-(\d+)Hari$/i.exec((p || "").trim());
  return m ? { count: Number(m[1]), days: Number(m[2]) } : null;
};
const parseHpSet = (pkg) => (pkg && Number(pkg.codes_per_set) > 1 ? Number(pkg.codes_per_set) : null);

export default function Vouchers() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const { selected, router, routers, setSelected } = useRouters();
  const [batches, setBatches] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [routerFilter, setRouterFilter] = useState("all");
  const [detailBatch, setDetailBatch] = useState(null);
  const [detailVouchers, setDetailVouchers] = useState(null);
  const showSisa = (detailVouchers || []).some((v) => v.sisa !== null && v.sisa !== undefined);
  const [profiles, setProfiles] = useState([]);
  const [provisioning, setProvisioning] = useState("");

  // 1-klik provision: buat profile "belum di router" ke MikroTik (PUT bersifat upsert)
  const provisionProfile = async (p) => {
    setProvisioning(p.profile);
    try {
      const { data } = await api.put(`/routers/${selected}/hotspot-profiles/${encodeURIComponent(p.profile)}`, {
        name: p.profile, rate_limit: p.rate_limit || "", validity: p.validity || "", time_limit: p.time_limit || "",
        data_limit: p.data_limit || "", modal: p.modal || 0, jual: p.jual || 0, lock_user: p.lock_user || "Enable",
        share_user: p.shared_users || 0, codes_per_set: p.codes_per_set || 1, show_price: p.show_price !== false,
      });
      toast.success(data.router === "created" ? `Profile ${p.profile} berhasil dibuat di router` : `Profile ${p.profile} tersimpan`);
      const r = await api.get(`/routers/${selected}/hotspot-profiles`);
      setPackages(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setProvisioning("");
    }
  };
  const [agents, setAgents] = useState([]);
  const [checked, setChecked] = useState({});
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState(GEN_DEFAULT);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(null);
  const [detailPkgs, setDetailPkgs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [nhpLock, setNhpLock] = useState(true);
  useEffect(() => { api.get("/settings/nhp-lock").then((r) => setNhpLock(r.data.enabled)).catch(() => {}); }, []);
  const hpSet = parseSeragam(gen.profile)?.count || parseHpSet(packages.find((x) => x.profile === gen.profile)) || 1;
  const hpLocked = !isAdmin && hpSet > 1 && nhpLock;
  useEffect(() => { if (hpLocked && Number(gen.count) !== 1) setGen((g) => ({ ...g, count: 1 })); }, [hpLocked]); // eslint-disable-line
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkgForm, setPkgForm] = useState(null);
  const [pkgSearch, setPkgSearch] = useState("");
  const [pkgPerPage, setPkgPerPage] = useState("10");
  const [pkgPage, setPkgPage] = useState(0);
  const [pkgDelete, setPkgDelete] = useState(null);
  const [savingPkg, setSavingPkg] = useState(false);
  const [pkgPinOpen, setPkgPinOpen] = useState(false);
  const [pkgSel, setPkgSel] = useState([]);
  const [pkgBulkOpen, setPkgBulkOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("profiles") === "1") {
      setPkgForm(null);
      setPkgOpen(true);
      setSearchParams({}, { replace: true });
      toast.info("Menampilkan tabel profile voucher cabang ini");
    } else if (searchParams.get("add-voucher") === "1") {
      setGen({ ...GEN_DEFAULT });
      setGenOpen(true);
      setSearchParams({}, { replace: true });
      toast.info("Silakan buat voucher baru untuk cabang ini");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sellOpen, setSellOpen] = useState(false);
  const [sell, setSell] = useState({ price: 0, payment_method: "tunai", customer: "", sold_by: "", commissions: [] });
  const [printData, setPrintData] = useState(null);
  const [usageSyncing, setUsageSyncing] = useState(false);

  const loadBatches = useCallback(() => {
    if (!routers.length) return;
    api.get("/vouchers/batches-all")
      .then((res) => setBatches(res.data.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))))
      .catch(() => {});
  }, [routers]);

  const loadDetail = useCallback((b) => {
    if (!b) return;
    setDetailVouchers(null);
    api.get(`/routers/${b.router_id}/vouchers`, { params: { batch_id: b.batch_id } })
      .then((r) => {
        const rows = r.data.filter((v) => v.batch_id === b.batch_id);
        setDetailVouchers(rows);
        if (rows.some((v) => v.router_offline)) toast.warning("Router tidak merespons — kode ditampilkan dari database (status online/terpakai tidak real-time).", { duration: 8000 });
      })
      .catch((e) => { setDetailVouchers([]); toast.error(errMsg(e)); });
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);
  // Deep-link dari Transaksi Voucher: /vouchers?router=<id>&batch=<batch_id> → langsung buka detail batch (bisa di tab baru)
  const deepBatch = searchParams.get("batch");
  const deepRouter = searchParams.get("router");
  useEffect(() => {
    if (!deepBatch || !batches.length) return;
    const b = batches.find((x) => x.batch_id === deepBatch && (!deepRouter || x.router_id === deepRouter));
    if (b) {
      if (deepRouter && deepRouter !== selected) setSelected(deepRouter);
      openBatch(b);
    } else toast.error(`Batch ${deepBatch} tidak ditemukan`);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepBatch, batches]);
  useEffect(() => { setChecked({}); }, [detailBatch?.batch_id]);
  useEffect(() => {
    if (!selected) return;
    setProfiles([]);
    setProfilesLoading(true);
    api.get(`/routers/${selected}/profiles`).then((r) => {
      const hs = r.data.hotspot || [];
      setProfiles(hs);
      setGen((g) => (hs.includes(g.profile) ? g : { ...g, profile: hs[0] || "" }));
    }).catch(() => {}).finally(() => setProfilesLoading(false));
    api.get(`/routers/${selected}/hotspot-profiles`).then((r) => setPackages(r.data)).catch((e) => { setPackages([]); toast.error(errMsg(e)); });
  }, [selected]);
  useEffect(() => { api.get("/agents").then((r) => setAgents(r.data)).catch(() => {}); }, []);
  const [hidingAll, setHidingAll] = useState(false);
  const [priceAllDlg, setPriceAllDlg] = useState(null); // {show, exclude:Set, q}
  const toggleShowPriceAll = () => setPriceAllDlg({ show: !packages.some((p) => p.show_price !== false), exclude: new Set(), q: "" });
  const submitShowPriceAll = async () => {
    const { show, exclude } = priceAllDlg;
    setHidingAll(true);
    try {
      const { data } = await api.put("/voucher-packages/show-price-all", { show_price: show, exclude_router_ids: [...exclude] });
      toast.success(`${show ? "Harga jual ditampilkan" : "Harga jual disembunyikan"} pada ${data.updated} profil voucher${data.excluded ? ` · ${data.excluded} cabang dikecualikan` : ""}`);
      const r = await api.get(`/routers/${selected}/hotspot-profiles`); setPackages(r.data); setPriceAllDlg(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setHidingAll(false); }
  };


  const openBatch = (b) => { setDetailBatch(b); loadDetail(b); setDetailPkgs([]); api.get(`/routers/${b.router_id}/hotspot-profiles`).then((r) => setDetailPkgs(r.data)).catch(() => {}); };
  const backToList = () => { setDetailBatch(null); setDetailVouchers(null); setChecked({}); loadBatches(); };

  const generate = async (e, cont = null) => {
    e?.preventDefault?.();
    const g = cont ? cont.gen : gen;
    const reqCount = cont ? cont.count : Number(gen.count);
    setGenerating(true);
    setGenProgress({ done: 0, total: reqCount || 0 });
    try {
      const useAsync = reqCount > 10; // batch kecil: sinkron — tanpa job, tanpa risiko "Job tidak ditemukan"
      const { data } = await api.post(`/routers/${selected}/vouchers/generate`, { ...g, count: reqCount, length: Number(g.length), async_job: useAsync, continue_batch: cont ? cont.batch_id : "" });
      let result = data;
      if (useAsync) {
        const jobId = data.job_id;
        let miss404 = 0;
        result = await new Promise((resolve, reject) => {
          const t = setInterval(async () => {
            try {
              const r = await api.get(`/vouchers/generate-status/${jobId}`);
              miss404 = 0;
              setGenProgress({ done: r.data.done || 0, total: r.data.total || reqCount, message: r.data.stalled ? r.data.message : "" });
              if (r.data.status === "done") { clearInterval(t); resolve(r.data); }
              else if (r.data.status === "error") { clearInterval(t); reject(new Error(r.data.error || "Gagal membuat voucher")); }
            } catch (err) {
              // 404 bisa terjadi sesaat bila request mendarat di proses lain — coba ulang sebelum menyerah
              miss404 += 1;
              if (err?.response?.status !== 404 || miss404 > 5) { clearInterval(t); reject(err); }
            }
          }, 800);
        });
      }
      toast.success(`${result.count} voucher berhasil dibuat`);
      if (result.warning) toast.warning(result.warning, { duration: 10000 });
      setGenOpen(false);
      const prevCount = cont ? cont.prevCount : 0;
      const prevModal = cont ? cont.prevModal : 0;
      const totalCount = prevCount + Number(result.count);
      // sisa yang belum jadi — untuk opsi "Lanjutkan Buat" agar jumlah genap sesuai permintaan
      const target = cont ? cont.target : (result.total_requested || totalCount);
      const failed = Math.max(0, target - totalCount);
      const codesPerSet = cont ? cont.codesPerSet : Math.max(1, Math.round((result.total_requested || result.count) / Number(gen.count)));
      openBatch({ router_id: selected, router_name: router?.name, dns_name: router?.dns_name, batch_id: result.batch_id, count: totalCount, modal: prevModal + (result.price || 0) * Number(result.count), profile: result.profile, created_at: cont ? cont.createdAt : new Date().toISOString(),
        remaining: failed, continueInfo: failed > 0 ? { gen: { ...g }, target, codesPerSet } : null });
      loadBatches();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Gagal membuat voucher"); } finally { setGenerating(false); setGenProgress(null); }
  };

  const continueGenerate = () => {
    const info = detailBatch?.continueInfo;
    if (!info || !detailBatch.remaining) return;
    const count = Math.ceil(detailBatch.remaining / info.codesPerSet);
    generate(null, { gen: info.gen, count, batch_id: detailBatch.batch_id, prevCount: detailBatch.count, prevModal: detailBatch.modal, target: info.target, codesPerSet: info.codesPerSet, createdAt: detailBatch.created_at });
  };

  const selectedNames = Object.keys(checked).filter((k) => checked[k]);

  const showPriceFor = (profile) => { const p = detailPkgs.find((x) => x.profile === profile) || packages.find((x) => x.profile === profile); return p ? p.show_price !== false : true; };

  const openSell = () => {
    const first = (detailVouchers || []).find((v) => v.name === selectedNames[0]);
    setSell({ price: first?.jual ?? first?.price ?? 0, payment_method: "tunai", customer: "", sold_by: "", commissions: [] });
    setSellOpen(true);
  };

  const doSell = async (e) => {
    e.preventDefault();
    try {
      const { data: sale } = await api.post(`/routers/${detailBatch.router_id}/vouchers/sell`, {
        names: selectedNames, price: Number(sell.price), payment_method: sell.payment_method,
        customer: sell.customer, sold_by: sell.sold_by,
        commissions: sell.commissions.filter((c) => c.agent_code && c.amount > 0),
      });
      setSellOpen(false);
      setChecked({});
      if (sell.payment_method === "midtrans") {
        paySaleWithMidtrans(sale, sell.customer, () => { loadDetail(detailBatch); loadBatches(); });
      } else {
        toast.success(`Penjualan ${rupiah(sale.total)} tercatat`);
        loadDetail(detailBatch); loadBatches();
      }
    } catch (err) { toast.error(errMsg(err)); }
  };

  const downloadPDF = async () => {
    try {
      await downloadVoucherPdf({ vouchers: detailVouchers || [], routerName: detailBatch.router_name, profile: detailBatch.profile || detailVouchers?.[0]?.profile, createdAt: detailBatch.created_at, showPrice: showPriceFor(detailBatch.profile) });
      toast.success("PDF voucher diunduh");
    } catch (err) { toast.error("Gagal membuat PDF"); }
  };

  const buildVoucherCanvas = (list) => {
    const pxmm = 12, cols = 10, margin = 1.5 * pxmm, gap = 1 * pxmm;
    const cw = 28.5 * pxmm, ch = 19.8 * pxmm;
    const rows = Math.ceil(list.length / cols);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(297 * pxmm);
    canvas.height = Math.round(margin * 2 + rows * ch + (rows - 1) * gap);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#000000";
    const stamp = fmtCreated(detailBatch.created_at);
    const showPrice = showPriceFor(detailBatch.profile);
    list.forEach((v, i) => {
      const x = margin + (i % cols) * (cw + gap);
      const y = margin + Math.floor(i / cols) * (ch + gap);
      ctx.strokeStyle = "#4b5563";
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, cw, ch);
      ctx.fillStyle = "#000000";
      ctx.font = "bold 25px Arial";
      ctx.textAlign = "left";
      ctx.fillText((detailBatch.router_name || "FASNET WIFI").toUpperCase(), x + 1.5 * pxmm, y + 3.8 * pxmm);
      ctx.textAlign = "right";
      if (showPrice) ctx.fillText(`Rp${num(v.jual ?? v.price)}`, x + cw - 1.5 * pxmm, y + 3.8 * pxmm);
      const code = String(v.name);
      ctx.font = `bold ${code.length > 10 ? 38 : code.length > 8 ? 47 : 55}px Arial`;
      ctx.textAlign = "center";
      ctx.fillText(code, x + cw / 2, y + 9.8 * pxmm);
      ctx.font = "23px Arial";
      ctx.textAlign = "left";
      ctx.fillText(String(detailBatch.profile || v.profile || "").toUpperCase(), x + 1.5 * pxmm, y + 14.2 * pxmm);
      ctx.textAlign = "right";
      ctx.fillText("login: fas.net", x + cw - 1.5 * pxmm, y + 14.2 * pxmm);
      ctx.font = "19px Arial";
      ctx.textAlign = "center";
      ctx.fillText(`Cetak: ${stamp} [${i + 1}]`, x + cw / 2, y + 17.8 * pxmm);
    });
    return canvas;
  };

  const voucherFileName = (list) => {
    const branch = String(detailBatch.router_name || "FASNET").replace(/\s+/g, "").toUpperCase();
    const profile = String(detailBatch.profile || list[0]?.profile || "VOUCHER").replace(/\s+/g, "").toUpperCase();
    const d = detailBatch.created_at ? new Date(detailBatch.created_at) : new Date();
    const tgl = `${String(d.getDate()).padStart(2, "0")}${BULAN[d.getMonth()]}${d.getFullYear()}`;
    const rand = Math.floor(1000000000 + Math.random() * 9000000000);
    return `vcr-${branch}-${profile}-${tgl}-${list.length}pcs-${rand}.png`;
  };

  const downloadImage = async () => {
    try {
      const list = detailVouchers || [];
      if (!list.length) return;
      const canvas = buildVoucherCanvas(list);
      canvas.toBlob((blob) => {
        if (!blob) { toast.error("Gagal membuat gambar"); return; }
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = voucherFileName(list);
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Gambar voucher diunduh");
      }, "image/png");
    } catch (err) { toast.error("Gagal membuat gambar"); }
  };

  const shareWhatsApp = async () => {
    try {
      const list = detailVouchers || [];
      if (!list.length) return;
      const canvas = buildVoucherCanvas(list);
      const profile = String(detailBatch.profile || list[0]?.profile || "").toUpperCase();
      const text = [
        `Voucher WiFi ${detailBatch.router_name || "FASNET"}${profile ? ` — ${profile}` : ""}`,
        ...list.map((v, i) => `${i + 1}. ${v.name}`),
        "Login: fas.net",
      ].join("\n");
      canvas.toBlob(async (blob) => {
        if (!blob) { toast.error("Gagal membuat gambar"); return; }
        const file = new File([blob], voucherFileName(list), { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], text, title: "Voucher WiFi FASNET" });
            toast.success("Gambar voucher dibagikan");
          } catch (err) {
            if (err?.name !== "AbortError") toast.error("Gagal membagikan voucher");
          }
        } else {
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = file.name;
          a.click();
          URL.revokeObjectURL(a.href);
          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
          toast.info("Gambar diunduh — lampirkan ke chat WhatsApp yang terbuka");
        }
      }, "image/png");
    } catch (err) { toast.error("Gagal membagikan voucher"); }
  };

  const [restoring, setRestoring] = useState(false);
  const [completing, setCompleting] = useState(false);
  const lengkapiBatch = async () => {
    if (!window.confirm(`Lengkapi kode voucher batch ${detailBatch?.batch_id} di router ${detailBatch?.router_name || ""}?\nSistem akan: menarik kode batch yang ada di router tapi belum tercatat, memulihkan kode yang hilang di router, lalu membuat sisa kode sampai jumlah yang dibayar. Saldo tidak dipotong / tidak direfund.`)) return;
    setCompleting(true);
    try {
      const { data } = await api.post(`/routers/${detailBatch.router_id}/vouchers/batch/${detailBatch.batch_id}/lengkapi`);
      toast.success(`Batch ${data.total}/${data.target} tercatat • di router ${data.on_router}${data.used ? ` • ${data.used} sudah dipakai/habis (wajar tidak ada di router)` : ""} — ${data.from_router} ditarik dari router, ${data.restored} dipulihkan, ${data.created} dibuat baru`, { duration: 12000 });
      loadDetail(detailBatch);
      loadBatches();
    } catch (e) { toast.error(errMsg(e)); } finally { setCompleting(false); }
  };
  const restoreMissing = async () => {
    const n = (detailVouchers || []).filter((v) => v.missing).length;
    if (!window.confirm(`Buat kembali ${n} kode voucher yang hilang ke router ${detailBatch?.router_name || ""}? Kode & password sama, saldo tidak dipotong lagi.`)) return;
    setRestoring(true);
    try {
      const { data } = await api.post(`/routers/${detailBatch.router_id}/vouchers/batch/${detailBatch.batch_id}/restore-missing`);
      toast.success(`${data.added} kode dibuat kembali di router${data.skipped_used ? ` — ${data.skipped_used} kode pernah login/habis dilewati` : ""}`);
      loadDetail(detailBatch);
    } catch (e) { toast.error(errMsg(e)); } finally { setRestoring(false); }
  };

  const copyCodes = async () => {
    try {
      const profile = String(detailBatch?.profile || detailVouchers?.[0]?.profile || "").toUpperCase();
      const text = (detailVouchers || [])
        .map((v, i) => (profile ? `${i + 1}. ${v.name}   ${profile}` : `${i + 1}. ${v.name}`))
        .join("\n");
      await navigator.clipboard.writeText(text);
      toast.success(`${(detailVouchers || []).length} kode voucher disalin`);
    } catch {
      toast.error("Gagal menyalin kode");
    }
  };

  const syncUsage = async () => {
    setUsageSyncing(true);
    const t0 = Date.now();
    try {
      await api.post("/vouchers/usage-scan");
      toast.info("Sinkronisasi pemakaian voucher dimulai...");
      const poll = async () => {
        try {
          const { data } = await api.get("/vouchers/usage-scan/status");
          if (data.status === "done" && new Date(data.finished_at).getTime() >= t0) {
            setUsageSyncing(false);
            toast.success(`Sinkronisasi selesai: ${data.stamped ?? 0} voucher baru tercatat terpakai`);
            loadBatches();
            if (detailBatch) loadDetail(detailBatch);
            return;
          }
        } catch { /* abaikan, coba lagi */ }
        if (Date.now() - t0 < 90000) setTimeout(poll, 3000);
        else setUsageSyncing(false);
      };
      setTimeout(poll, 3000);
    } catch (err) {
      setUsageSyncing(false);
      toast.error(errMsg(err));
    }
  };

  const openPackages = () => { setPkgForm(null); setPkgSearch(""); setPkgPage(0); setPkgDelete(null); setPkgOpen(true); };

  const saveProfile = async () => {
    setSavingPkg(true);
    try {
      const payload = { name: pkgForm.name.trim(), rate_limit: pkgForm.rate_limit, validity: pkgForm.validity, time_limit: pkgForm.time_limit, data_limit: pkgForm.data_limit, modal: Number(pkgForm.modal) || 0, jual: Number(pkgForm.jual) || 0, lock_user: pkgForm.lock_user, share_user: Number(pkgForm.share_user) || 0, codes_per_set: Math.max(1, Number(pkgForm.codes_per_set) || 1), show_price: pkgForm.show_price !== false, hide_from_agents: !!pkgForm.hide_from_agents };
      if (pkgForm.editing) {
        await api.put(`/routers/${selected}/hotspot-profiles/${encodeURIComponent(pkgForm.editing)}`, payload);
      } else {
        await api.post(`/routers/${selected}/hotspot-profiles`, payload);
      }
      toast.success("Profile voucher tersimpan");
      setPkgForm(null);
      const { data } = await api.get(`/routers/${selected}/hotspot-profiles`);
      setPackages(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setSavingPkg(false); }
  };

  const doDeleteProfile = async () => {
    try {
      await api.delete(`/routers/${selected}/hotspot-profiles/${encodeURIComponent(pkgDelete)}`);
      toast.success(`Profile ${pkgDelete} dihapus`);
      setPkgDelete(null);
      const { data } = await api.get(`/routers/${selected}/hotspot-profiles`);
      setPackages(data);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const doBulkDeleteProfiles = async () => {
    setBulkDeleting(true);
    try {
      const { data } = await api.post(`/routers/${selected}/hotspot-profiles/bulk-delete`, { names: pkgSel });
      toast.success(`${data.deleted} profile dihapus${data.router_ok ? "" : " (router offline, hanya daftar harga)"}`);
      setPkgSel([]);
      const res = await api.get(`/routers/${selected}/hotspot-profiles`);
      setPackages(res.data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBulkDeleting(false); }
  };

  const batchesFiltered = batches.filter((b) =>
    (routerFilter === "all" || b.router_id === routerFilter) &&
    (!keyword || (b.profile || "").toLowerCase().includes(keyword.toLowerCase()) || (b.batch_id || "").toLowerCase().includes(keyword.toLowerCase())));

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Data Voucher</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {detailBatch ? <>Data Voucher / Transaksi Voucher Fasnet / <span className="text-sky-500">Detail</span></> : (isAdmin ? "Transaksi Voucher Wifi semua cabang" : "Voucher Wifi yang Anda buat")}
          </p>
        </div>
        <div className="flex gap-2">
          {detailBatch && (
            <Button variant="outline" disabled={!selectedNames.length} onClick={openSell} data-testid="btn-sell-vouchers">
              <ShoppingCart className="w-4 h-4 mr-1.5" /> Jual ({selectedNames.length})
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={syncUsage} disabled={usageSyncing} data-testid="btn-sync-usage">
              <RefreshCcw className={`w-4 h-4 mr-1.5 ${usageSyncing ? "animate-spin" : ""}`} /> {usageSyncing ? "Menyinkronkan..." : "Sync Pemakaian"}
            </Button>
          )}
          {isAdmin && <RekonsiliasiLogButton />}
          {isAdmin && (
            <Button variant="outline" onClick={openPackages} disabled={!selected} data-testid="btn-open-packages">
              <Settings2 className="w-4 h-4 mr-1.5" /> Pengaturan Paket
            </Button>
          )}
          <Button onClick={() => setGenOpen(true)} disabled={!selected} data-testid="btn-open-generate">
            <Zap className="w-4 h-4 mr-1.5" /> Buat Voucher
          </Button>
        </div>
      </div>

      {!detailBatch && (
        <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="batch-card">
          <CardHeader><CardTitle className="text-base font-heading">Transaksi Voucher Wifi</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0 space-y-4">
            <div className="flex gap-3 items-end flex-wrap">
              <div className="space-y-1.5">
                <Label>Keyword</Label>
                <Input className="w-56" placeholder="Nama Paket / No Voucher" value={keyword} onChange={(e) => setKeyword(e.target.value)} data-testid="input-batch-keyword" />
              </div>
              <div className="space-y-1.5">
                <Label>Router</Label>
                <div className="w-44"><RouterCombobox routers={routers} value={routerFilter === "all" ? "ALL" : routerFilter} onChange={(v) => setRouterFilter(v === "ALL" ? "all" : v)} allLabel="ALL" testid="select-batch-router" /></div>
              </div>
              <Button variant="destructive" data-testid="btn-batch-cari"><Search className="w-4 h-4 mr-1.5" /> Cari</Button>
            </div>
            <Table className="min-w-[980px]" data-testid="batch-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead><TableHead>Created Date</TableHead><TableHead>No Voucher</TableHead><TableHead>Paket Nama</TableHead>
                  <TableHead className="text-right">Jumlah Voucher</TableHead><TableHead className="text-right">Harga Modal</TableHead>
                  <TableHead className="text-right">Harga Jual</TableHead><TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!batchesFiltered.length && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground" data-testid="batch-empty">Belum ada batch voucher</TableCell></TableRow>}
                {batchesFiltered.map((b, i) => (
                  <TableRow key={`${b.router_id}-${b.batch_id}`} data-testid={`batch-row-${b.batch_id}`}>
                    <TableCell className="font-mono text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{fmtCreated(b.created_at)}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap font-semibold text-sky-500" data-testid={`batch-no-${b.batch_id}`}>{b.batch_id}</TableCell>
                    <TableCell>
                      <div className="font-medium">{b.profile || "-"}</div>
                      {b.valid_days > 0 && (
                        <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded border bg-[#F04E23]/10 text-[#F04E23] border-[#F04E23]/30 text-[9px] font-mono" data-testid={`batch-seragam-${b.batch_id}`}>Seragam • {b.valid_days} hari sejak login</span>
                      )}
                      {routerFilter === "all" && <div className="text-[10px] font-mono text-muted-foreground">{b.router_name}</div>}
                      {isAdmin && (b.creator_name || b.creator_code) && (
                        <div className="text-[10px] text-muted-foreground" data-testid={`batch-creator-${b.batch_id}`}>Pembuat: {b.creator_name || "-"}{b.creator_code ? ` [${b.creator_code}]` : ""}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {b.count}
                      {b.missing > 0 && <div className="text-[10px] text-rose-400 whitespace-nowrap" title="Kode tercatat di database tapi tidak ditemukan di router — tidak dipulihkan otomatis; agen komplain ke admin, admin cek riwayat lalu pulihkan manual" data-testid={`batch-missing-${b.batch_id}`}>{b.missing} belum di router</div>}
                    </TableCell>
                    <TableCell className="text-right font-mono">{num(b.modal)}</TableCell>
                    <TableCell className="text-right font-mono">{num(b.jual)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" className="bg-lime-600 hover:bg-lime-500 text-white" onClick={() => openBatch(b)} data-testid={`btn-open-batch-${b.batch_id}`}>
                        <FolderOpen className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {generating && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/75 backdrop-blur-sm" data-testid="generating-overlay">
          <div className="relative w-28 h-28">
            <div className="absolute inset-0 rounded-full border-4 border-[#F04E23]/20 border-t-[#F04E23] animate-spin" />
            <img src="/fibi.webp" alt="FIBI" className="absolute inset-2 w-24 h-24 rounded-full object-cover" />
          </div>
          <p className="mt-4 text-sm font-medium text-white animate-pulse">{genProgress?.message ? "Server sedang diperbarui..." : "FIBI sedang membuat voucher..."}</p>
          {genProgress?.message && <p className="mt-1 max-w-xs text-center text-[11px] text-amber-300" data-testid="gen-stalled-msg">{genProgress.message}</p>}
          {genProgress && (
            <>
              <p className="mt-2 text-2xl font-bold font-mono text-white" data-testid="gen-progress-text">{genProgress.done}<span className="text-white/50 text-base font-normal">/{genProgress.total} voucher</span></p>
              <div className="mt-3 w-64 h-2 rounded-full bg-white/10 overflow-hidden" data-testid="gen-progress-bar">
                <div className="h-full bg-[#F04E23] rounded-full transition-all duration-300" style={{ width: `${genProgress.total ? Math.min(100, (genProgress.done / genProgress.total) * 100) : 0}%` }} />
              </div>
            </>
          )}
        </div>
      )}

      {detailBatch && (
        <div className="grid lg:grid-cols-[340px_1fr] gap-5 items-start" data-testid="voucher-detail-panel">          <Card className="bg-card border-border shadow-sm">
            <CardHeader><CardTitle className="text-base font-heading">Voucher Detail</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5"><Label className="text-muted-foreground">Nomor Batch</Label><Input value={detailBatch.batch_id} readOnly className="font-mono" data-testid="detail-batch-id" /></div>
              <div className="space-y-1.5"><Label className="text-muted-foreground">Nama Hotspot</Label><Input value={detailBatch.router_name} readOnly data-testid="detail-hotspot-name" /></div>
              <div className="space-y-1.5"><Label className="text-muted-foreground">Nama DNS</Label><Input value={detailBatch.dns_name || "-"} readOnly data-testid="detail-dns-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-muted-foreground">Jumlah Voucher</Label><Input value={detailBatch.count} readOnly data-testid="detail-count" /></div>
                <div className="space-y-1.5"><Label className="text-muted-foreground">Total Harga</Label><Input value={`Rp ${num(detailBatch.modal)}`} readOnly data-testid="detail-total" /></div>
              </div>
              {detailBatch.remaining > 0 && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="detail-partial-warning">
                  <div className="text-xs text-amber-700 dark:text-amber-300 font-medium">Hanya {detailBatch.count}/{detailBatch.continueInfo?.target} voucher jadi — {detailBatch.remaining} kode gagal dibuat (saldo selisih sudah dikembalikan).</div>
                  <Button className="w-full bg-amber-600 hover:bg-amber-500 text-white" onClick={continueGenerate} disabled={generating} data-testid="btn-continue-generate">
                    <RefreshCcw className={`w-4 h-4 mr-1.5 ${generating ? "animate-spin" : ""}`} /> {generating ? `Membuat... ${genProgress?.done ?? 0}/${genProgress?.total ?? detailBatch.remaining}` : `Lanjutkan Buat (${detailBatch.remaining} sisa)`}
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={downloadPDF} disabled={!detailVouchers?.length} data-testid="btn-download-pdf">
                  <Download className="w-4 h-4 mr-1.5" /> PDF
                </Button>
                <Button className="bg-sky-600 hover:bg-sky-500" onClick={copyCodes} disabled={!detailVouchers?.length} data-testid="btn-copy-codes">
                  <Copy className="w-4 h-4 mr-1.5" /> Copy
                </Button>
                <Button className="bg-violet-600 hover:bg-violet-500" onClick={downloadImage} disabled={!detailVouchers?.length} data-testid="btn-download-image">
                  <ImageDown className="w-4 h-4 mr-1.5" /> Gambar
                </Button>
                <Button className="bg-[#25D366] hover:bg-[#1fb857] text-white" onClick={shareWhatsApp} disabled={!detailVouchers?.length} data-testid="btn-share-wa">
                  <Share2 className="w-4 h-4 mr-1.5" /> Share WA
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="destructive" onClick={() => setPrintData({ vouchers: detailVouchers || [], profile: detailBatch.profile, price: detailVouchers?.[0]?.jual ?? detailBatch.price, routerName: detailBatch.router_name, createdAt: detailBatch.created_at, mode: "thermal", showPrice: showPriceFor(detailBatch.profile) })} disabled={!detailVouchers?.length} data-testid="btn-print-batch-thermal">
                  <Printer className="w-4 h-4 mr-1.5" /> Print Thermal
                </Button>
                <Button variant="destructive" onClick={() => setPrintData({ vouchers: detailVouchers || [], profile: detailBatch.profile, price: detailVouchers?.[0]?.jual ?? detailBatch.price, routerName: detailBatch.router_name, createdAt: detailBatch.created_at, mode: "desktop", showPrice: showPriceFor(detailBatch.profile) })} disabled={!detailVouchers?.length} data-testid="btn-print-batch-desktop">
                  <Printer className="w-4 h-4 mr-1.5" /> Print Desktop
                </Button>
              </div>
              <Button variant="outline" className="w-full" onClick={backToList} data-testid="btn-back-batch">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Kembali
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-card border-border shadow-sm overflow-x-auto">
            <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
              <CardTitle className="text-base font-heading">Voucher Data <span className="ml-2 font-mono text-xs text-muted-foreground font-normal" data-testid="detail-title-batch">{detailBatch.batch_id}</span>{detailVouchers?.some((v) => v.router_offline) && <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-mono bg-amber-500/15 text-amber-400 border border-amber-500/30 align-middle" data-testid="detail-router-offline">ROUTER OFFLINE — data dari database</span>}</CardTitle>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10" disabled={completing || restoring} onClick={lengkapiBatch} title="Tarik kode batch dari router + pulihkan kode hilang + buat sisa kode sampai jumlah yang dibayar (tanpa potong saldo)" data-testid="btn-lengkapi-batch">
                    {completing ? "Melengkapi..." : "Lengkapi Kode Voucher"}
                  </Button>
                  {detailVouchers?.some((v) => v.missing) && (<>
                  <span className="text-[10px] text-muted-foreground hidden md:inline" data-testid="restore-hint">Kode yang pernah login/habis otomatis dilewati</span>
                  <Button size="sm" className="bg-rose-600 hover:bg-rose-500" disabled={restoring || completing} onClick={restoreMissing} data-testid="btn-restore-missing">
                    {restoring ? "Memulihkan..." : `Pulihkan ${detailVouchers.filter((v) => v.missing).length} kode ke Router`}
                  </Button></>)}
                </div>
              )}
            </CardHeader>
            <Table className="min-w-[880px]" data-testid="detail-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={!!detailVouchers?.length && detailVouchers.every((v) => checked[v.name])}
                      onCheckedChange={(v) => { const next = {}; if (v) (detailVouchers || []).forEach((x) => { next[x.name] = true; }); setChecked(next); }}
                      data-testid="checkbox-select-all" />
                  </TableHead>
                  <TableHead className="w-10">#</TableHead><TableHead>Nama Paket</TableHead><TableHead>Kode Voucher</TableHead>
                  <TableHead title="Tanggal login pertama kode ini">Tanggal Login</TableHead>
                  {showSisa && <TableHead>Sisa</TableHead>}
                  <TableHead className="text-right">Harga Modal</TableHead>
                  <TableHead className="text-right">Harga Jual</TableHead><TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailVouchers === null && <TableRow><TableCell colSpan={9} className="p-0"><FibiLoading /></TableCell></TableRow>}
                {detailVouchers !== null && !detailVouchers.length && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground" data-testid="voucher-empty">Tidak ada voucher di batch ini</TableCell></TableRow>}
                {(detailVouchers || []).map((v, i) => (
                  <TableRow key={v.name} data-testid={`voucher-row-${v.name}`}>
                    <TableCell><Checkbox checked={!!checked[v.name]} onCheckedChange={(c) => setChecked({ ...checked, [v.name]: !!c })} data-testid={`checkbox-${v.name}`} /></TableCell>
                    <TableCell className="font-mono text-muted-foreground">{i + 1}</TableCell>
                    <TableCell>{v.profile}</TableCell>
                    <TableCell className="font-mono font-bold">{v.name}{v.online && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 align-middle" title="Sedang ONLINE — ada sesi hotspot aktif memakai kode ini" />}{v.missing && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-rose-500/15 text-rose-400 border border-rose-500/30 align-middle" title="Kode tercatat & sudah dibayar, tetapi tidak ada lagi di router" data-testid={`voucher-missing-${v.name}`}>TIDAK ADA DI ROUTER</span>}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap" data-testid={`voucher-used-${v.name}`}>
                      {v.used_at ? <span className="text-emerald-500">{fmtCreated(v.used_at)}</span> : <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    {showSisa && <TableCell className="font-mono text-xs whitespace-nowrap" data-testid={`voucher-sisa-${v.name}`}><SisaVoucher s={v.sisa} disabled={v.disabled} /></TableCell>}
                    <TableCell className="text-right font-mono">{num(v.price)}</TableCell>
                    <TableCell className="text-right font-mono">{num(v.jual ?? v.price)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" className="text-sky-400" onClick={() => setPrintData({ vouchers: [v], profile: v.profile, price: v.jual ?? v.price, routerName: detailBatch.router_name, createdAt: detailBatch.created_at, mode: "thermal", showPrice: showPriceFor(v.profile) })} data-testid={`btn-print-${v.name}`}><Printer className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md max-h-[85vh] overflow-y-auto p-0 gap-0 rounded-2xl" data-testid="dialog-generate">
          <DialogHeader className="px-4 py-3 sm:px-5 sm:py-3.5 bg-accent/60 border-b border-border">
            <DialogTitle className="text-sm sm:text-base font-heading">Buat Voucher Baru</DialogTitle>
          </DialogHeader>
          <form onSubmit={generate} className="p-4 sm:p-5 space-y-3 sm:space-y-4">
            <div className="space-y-1.5"><Label className="text-xs sm:text-sm">Paket Wifi</Label>
              <select value={gen.profile} onChange={(e) => setGen({ ...gen, profile: e.target.value })} disabled={profilesLoading} data-testid="select-gen-profile"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60">
                {profiles.length === 0 && <option value="" data-testid="gen-profile-opt-none">{profilesLoading ? "Memuat paket..." : "Belum ada paket di router ini"}</option>}
                {profiles.map((p) => <option key={p} value={p} data-testid={`gen-profile-opt-${p}`}>{p}</option>)}
              </select>
              {profilesLoading && (
                <p className="text-[11px] text-amber-500 animate-pulse" data-testid="gen-profile-loading">Memuat profile dari router — tunggu beberapa detik, sistem tidak error...</p>
              )}
            </div>
            {isAdmin && parseSeragam(gen.profile) && (
              <div className="rounded-xl bg-[#F04E23]/10 border border-[#F04E23]/30 px-4 py-3 text-xs text-[#F04E23] space-y-1" data-testid="gen-seragam-info">
                <p className="font-bold">Mode Voucher Seragam aktif</p>
                <p>1 set = {parseSeragam(gen.profile).count} kode berurutan otomatis (mis. kxwp1–kxwp{parseSeragam(gen.profile).count}). Lock User: Disable • Share User sesuai pengaturan profile (default: {parseSeragam(gen.profile).count}).</p>
                <p>Jumlah Voucher = jumlah set yang dibuat. Masa aktif {parseSeragam(gen.profile).days} hari dihitung sejak LOGIN PERTAMA — lewat itu kode otomatis terhapus dari daftar user MikroTik.</p>
              </div>
            )}
            {!parseSeragam(gen.profile) && parseHpSet(packages.find((x) => x.profile === gen.profile)) && (
              <div className="rounded-xl bg-sky-500/10 border border-sky-500/30 px-4 py-3 text-xs text-sky-400 space-y-1" data-testid="gen-hpset-info">
                <p className="font-bold">Paket {parseHpSet(packages.find((x) => x.profile === gen.profile))} kode per lembar</p>
                <p>1 lembar = {parseHpSet(packages.find((x) => x.profile === gen.profile))} kode berurutan otomatis (mis. abcd1–abcd{parseHpSet(packages.find((x) => x.profile === gen.profile))}), satu kode untuk tiap HP. Harga di bawah = harga per lembar/set.</p>
              </div>
            )}
            {hpLocked && (
              <HpCustomerPicker routerId={selected} codesPerSet={hpSet} value={gen.customer_id || ""} onChange={(v) => setGen({ ...gen, customer_id: v, count: 1 })} />
            )}
            <div className="space-y-1.5"><Label className="text-xs sm:text-sm">Jumlah Voucher</Label>
              <Input type="number" min="1" max="250" value={gen.count} data-testid="input-gen-count" disabled={hpLocked}
                onChange={(e) => setGen({ ...gen, count: e.target.value === "" ? "" : Math.min(250, Math.max(1, parseInt(e.target.value, 10) || 1)) })}
                onBlur={() => setGen({ ...gen, count: Math.min(250, Math.max(1, Number(gen.count) || 1)) })} />
              <p className="text-[11px] text-muted-foreground">{hpLocked ? "Paket banyak HP: 1 set per pelanggan / 30 hari" : "1 – 250 lembar"}</p>
            </div>
            {!parseSeragam(gen.profile) && !parseHpSet(packages.find((x) => x.profile === gen.profile)) && (
              <div className="space-y-1.5"><Label className="text-xs sm:text-sm">Tipe Random</Label>
                <select value={gen.chars} onChange={(e) => setGen({ ...gen, chars: e.target.value })} data-testid="select-gen-chars"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="mix" data-testid="gen-chars-opt-mix">aBc12</option>
                  <option value="alpha" data-testid="gen-chars-opt-alpha">abcd</option>
                  <option value="num" data-testid="gen-chars-opt-num">1234</option>
                </select>
              </div>
            )}
            <div className="border-t border-border pt-3 sm:pt-4 grid grid-cols-2 gap-2.5 sm:gap-3">
              <Button type="button" variant="outline" className="h-9 text-xs sm:text-sm" onClick={() => setGenOpen(false)} data-testid="btn-cancel-generate">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Batal
              </Button>
              <Button type="submit" className="h-9 text-xs sm:text-sm bg-emerald-500 hover:bg-emerald-600 text-white" disabled={generating || (hpLocked && !gen.customer_id)} data-testid="btn-confirm-generate">
                <Check className="w-3.5 h-3.5 mr-1.5" /> {generating ? "Membuat..." : "Buat Voucher"}
              </Button>
            </div>
          </form>

          {(() => {
            const pk = packages.find((x) => x.profile === gen.profile);
            // Harga modal paket seragam (mis. 4HP-30Hari) = harga TOTAL per set/lembar, bukan per kode
            const total = (pk?.modal || 0) * Number(gen.count || 1);
            const ROWS = [
              ["Hotspot", router?.name || "-"],
              ["DNS", router?.dns_name || router?.host || "-"],
              ["Nama Paket", gen.profile || "-"],
              ["Tipe Login", "Username = Password"],
              ["Harga Modal", pk ? rupiah(pk.modal) : "-"],
              ["Harga Jual", pk ? rupiah(pk.jual) : "-"],
            ];
            return (
              <div className="px-4 pb-4 sm:px-5 sm:pb-5" data-testid="gen-package-panel">
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="px-3 py-2 sm:px-4 sm:py-2.5 bg-accent/60 border-b border-border text-xs sm:text-sm font-heading font-semibold">Informasi Paket Wifi</div>
                  <div className="divide-y divide-border text-xs sm:text-sm">
                    {ROWS.map(([k, v]) => (
                      <div key={k} className="grid grid-cols-2 px-3 py-1.5 sm:px-4 sm:py-2" data-testid={`gen-info-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <div className="text-muted-foreground">{k}</div>
                        <div className="font-medium text-right">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {!pk && (
                  <div className="mt-3 text-xs text-amber-500" data-testid="gen-package-missing">
                    Profile ini belum punya paket harga — minta Admin Fasnet / Super Admin mengatur di Pengaturan Paket.
                  </div>
                )}
                <div className="mt-2.5 text-xs sm:text-sm" data-testid="gen-total-biaya">Biaya Total = <span className="font-bold text-emerald-600">{rupiah(total)}</span></div>
                <div className="mt-2.5 rounded-xl bg-sky-500/15 border border-sky-500/30 px-3 py-2.5 text-xs sm:text-sm text-sky-600" data-testid="gen-saldo-warning">
                  Untuk menyelesaikan transaksi ini, Anda harus memiliki saldo minimal <span className="font-bold">{rupiah(total)}</span>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={sellOpen} onOpenChange={setSellOpen}>
        <DialogContent data-testid="dialog-sell">
          <DialogHeader><DialogTitle>Jual {selectedNames.length} Voucher</DialogTitle></DialogHeader>
          <form onSubmit={doSell} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Harga per Voucher (Rp){!isAdmin && <span className="text-xs text-muted-foreground"> — sesuai pengaturan admin</span>}</Label>
              <Input type="number" min="0" value={sell.price} onChange={(e) => setSell({ ...sell, price: e.target.value })} required readOnly={!isAdmin} data-testid="input-sell-price" />
            </div>
            <div className="space-y-1.5"><Label>Pembayaran</Label>
              <select value={sell.payment_method} onChange={(e) => setSell({ ...sell, payment_method: e.target.value })} data-testid="select-sell-payment"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                <option value="tunai">Tunai</option>
                <option value="transfer">Transfer</option>
                <option value="midtrans">Midtrans (QRIS/GoPay)</option>
              </select>
            </div>
            <div className="space-y-1.5"><Label>Nama Pelanggan (opsional)</Label><Input value={sell.customer} onChange={(e) => setSell({ ...sell, customer: e.target.value })} data-testid="input-sell-customer" /></div>
            <CommissionPicker agents={agents} value={sell.commissions} onChange={(c) => setSell({ ...sell, commissions: c })} testid="sell-commission" />
            <div className="text-sm font-mono bg-accent/50 rounded-lg px-3 py-2">Total: {rupiah(Number(sell.price) * selectedNames.length)}</div>
            <Button type="submit" className="w-full" data-testid="btn-confirm-sell">Catat Penjualan</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={pkgOpen} onOpenChange={setPkgOpen}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="dialog-packages">
          {pkgForm === null ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <DialogHeader><DialogTitle>Tabel Data Profile</DialogTitle></DialogHeader>
                <div className="flex items-center gap-2">
                  {me?.is_owner && (
                    <Button variant="outline" className={packages.some((p) => p.show_price !== false) ? "border-rose-500/40 text-rose-400 hover:bg-rose-500/10" : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"} disabled={hidingAll} onClick={toggleShowPriceAll} title="Berlaku untuk SEMUA cabang/router sekaligus" data-testid="btn-toggle-price-all">
                      {packages.some((p) => p.show_price !== false) ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                      {hidingAll ? "Memproses…" : packages.some((p) => p.show_price !== false) ? "Jangan Tampilkan Harga Jual (Semua Cabang)" : "Tampilkan Harga Jual (Semua Cabang)"}
                    </Button>
                  )}
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => setPkgForm({ ...HS_PROFILE_EMPTY })} data-testid="btn-add-profile">
                    <Plus className="w-4 h-4 mr-1" /> Add Profile
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Router</Label>
                <Select value={selected} onValueChange={(v) => { setSelected(v); setPkgPage(0); setPkgSel([]); }}>
                  <SelectTrigger className="w-48" data-testid="pkg-router-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{routers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Select value={pkgPerPage} onValueChange={(v) => { setPkgPerPage(v); setPkgPage(0); }}>
                    <SelectTrigger className="w-20" data-testid="pkg-per-page"><SelectValue /></SelectTrigger>
                    <SelectContent>{["10", "25", "50", "100"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">records per page</span>
                </div>
                {pkgSel.length > 0 && (
                  <div className="flex items-center gap-2" data-testid="pkg-bulk-bar">
                    <span className="text-xs font-mono text-muted-foreground" data-testid="pkg-sel-count">{pkgSel.length} profile dipilih</span>
                    <Button size="sm" variant="outline" onClick={() => setPkgSel([])} data-testid="btn-pkg-sel-clear">Batal</Button>
                    <Button size="sm" className="bg-rose-500 hover:bg-rose-600 text-white" disabled={bulkDeleting} onClick={() => setPkgBulkOpen(true)} data-testid="btn-pkg-bulk-delete">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus {pkgSel.length} Profile
                    </Button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Search:</span>
                  <Input className="w-56" value={pkgSearch} onChange={(e) => { setPkgSearch(e.target.value); setPkgPage(0); }} data-testid="pkg-search" />
                </div>
              </div>
              {(() => {
                const q = pkgSearch.trim().toLowerCase();
                const filtered = packages.filter((p) => !q || [p.profile, p.rate_limit, p.validity, String(p.modal), String(p.jual)].some((f) => (f || "").toLowerCase().includes(q)));
                const per = Number(pkgPerPage);
                const pageCount = Math.max(1, Math.ceil(filtered.length / per));
                const cur = Math.min(pkgPage, pageCount - 1);
                const view = filtered.slice(cur * per, cur * per + per);
                const routerName = routers.find((r) => r.id === selected)?.name || "-";
                return (
                  <>
                    <div className="border border-border rounded-lg overflow-x-auto">
                      <Table className="min-w-[1080px]" data-testid="pkg-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8">
                              <input type="checkbox" className="h-4 w-4 accent-rose-500 cursor-pointer" title="Pilih semua (hasil filter)"
                                checked={filtered.length > 0 && filtered.every((p) => pkgSel.includes(p.profile))}
                                onChange={(e) => setPkgSel(e.target.checked ? filtered.map((p) => p.profile) : [])} data-testid="pkg-select-all" />
                            </TableHead>
                            <TableHead className="w-8">#</TableHead><TableHead>Router</TableHead><TableHead>Nama Paket</TableHead><TableHead>Rate Limit</TableHead>
                            <TableHead>Validity</TableHead><TableHead>Time Limit</TableHead><TableHead>Login Type</TableHead><TableHead>Data Limit</TableHead>
                            <TableHead>Harga Modal</TableHead><TableHead>Harga Jual</TableHead><TableHead>Lock User</TableHead><TableHead>Share User</TableHead><TableHead className="text-center">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {!view.length && <TableRow><TableCell colSpan={14} className="text-center py-8 text-muted-foreground" data-testid="pkg-empty">Belum ada profile voucher di router ini</TableCell></TableRow>}
                          {view.map((p, i) => (
                            <TableRow key={p.profile} data-testid={`pkg-row-${p.profile}`} className={pkgSel.includes(p.profile) ? "bg-rose-500/5" : ""}>
                              <TableCell>
                                <input type="checkbox" className="h-4 w-4 accent-rose-500 cursor-pointer" checked={pkgSel.includes(p.profile)}
                                  onChange={(e) => setPkgSel(e.target.checked ? [...pkgSel, p.profile] : pkgSel.filter((x) => x !== p.profile))} data-testid={`pkg-check-${p.profile}`} />
                              </TableCell>
                              <TableCell className="text-muted-foreground">{cur * per + i + 1}</TableCell>
                              <TableCell>{routerName}</TableCell>
                              <TableCell className="font-semibold">{p.profile}{p.hide_from_agents && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-500/15 text-amber-400 border border-amber-500/30" title="Disembunyikan dari semua agen" data-testid={`pkg-hidden-${p.profile}`}>HIDDEN</span>}{!p.on_router && (
                                <span className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-[10px] font-mono text-amber-500">belum di router</span>
                                  <button type="button" disabled={provisioning === p.profile} onClick={() => provisionProfile(p)}
                                    className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold bg-emerald-500/15 text-emerald-500 border border-emerald-500/40 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
                                    data-testid={`btn-provision-${p.profile}`}>
                                    {provisioning === p.profile ? "Membuat..." : "Buat di Router"}
                                  </button>
                                </span>
                              )}</TableCell>
                              <TableCell className="font-mono text-xs">{p.rate_limit || "-"}</TableCell>
                              <TableCell className="font-mono text-xs">{p.validity || "-"}</TableCell>
                              <TableCell className="font-mono text-xs">{p.time_limit || "-"}</TableCell>
                              <TableCell className="text-xs">Username = Password</TableCell>
                              <TableCell className="font-semibold">{p.data_limit || "unlimited"}</TableCell>
                              <TableCell className="font-mono">{rp(p.modal)}</TableCell>
                              <TableCell className="font-mono">{rp(p.jual)}</TableCell>
                              <TableCell>
                                <span className={`px-2 py-0.5 rounded text-xs font-mono ${p.lock_user === "Enable" ? "bg-emerald-500/10 text-emerald-500" : "bg-slate-500/10 text-slate-400"}`} data-testid={`pkg-lock-${p.profile}`}>{p.lock_user || "Enable"}</span>
                              </TableCell>
                              <TableCell className="font-mono text-xs" data-testid={`pkg-share-${p.profile}`}>{p.shared_users || "-"}</TableCell>
                              <TableCell className="text-center space-x-1 whitespace-nowrap">
                                <Button size="icon" className="bg-sky-500 hover:bg-sky-600 text-white h-8 w-8" title="Copy Profile" onClick={() => setPkgForm({ ...HS_PROFILE_EMPTY, rate_limit: p.rate_limit || "", validity: p.validity || "", time_limit: p.time_limit || "", data_limit: p.data_limit || "", modal: p.modal, jual: p.jual, lock_user: p.lock_user || "Enable", share_user: p.shared_users || 0, codes_per_set: p.codes_per_set || 1, show_price: p.show_price !== false })} data-testid={`btn-copy-profile-${p.profile}`}>
                                  <Copy className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" className="bg-emerald-500 hover:bg-emerald-600 text-white h-8 w-8" title="Edit Profile" onClick={() => setPkgForm({ ...HS_PROFILE_EMPTY, name: p.profile, rate_limit: p.rate_limit || "", validity: p.validity || "", time_limit: p.time_limit || "", data_limit: p.data_limit || "", modal: p.modal, jual: p.jual, lock_user: p.lock_user || "Enable", share_user: p.shared_users || 0, codes_per_set: (p.codes_per_set > 1 ? p.codes_per_set : parseSeragam(p.profile)?.count) || 1, show_price: p.show_price !== false, hide_from_agents: !!p.hide_from_agents, editing: p.profile })} data-testid={`btn-edit-profile-${p.profile}`}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" className="bg-rose-500 hover:bg-rose-600 text-white h-8 w-8" onClick={() => { setPkgDelete(p.profile); setPkgPinOpen(true); }} data-testid={`btn-del-profile-${p.profile}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span data-testid="pkg-showing">Showing {filtered.length ? cur * per + 1 : 0} to {Math.min((cur + 1) * per, filtered.length)} of {filtered.length} entries</span>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" disabled={cur === 0} onClick={() => setPkgPage(cur - 1)} data-testid="btn-pkg-prev">← Previous</Button>
                        <span className="px-3 py-1 rounded bg-blue-600 text-white text-xs font-mono" data-testid="pkg-page-num">{cur + 1}</span>
                        <Button size="sm" variant="outline" disabled={cur >= pageCount - 1} onClick={() => setPkgPage(cur + 1)} data-testid="btn-pkg-next">Next →</Button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          ) : (
            <div className="space-y-3">
              <DialogHeader><DialogTitle>{pkgForm.editing ? `Edit Profile: ${pkgForm.editing}` : "Add Profile"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Nama Paket</Label><Input value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} disabled={!!pkgForm.editing} placeholder="cth: 5JAM" data-testid="hpf-name" /></div>
                <div className="space-y-1.5"><Label>Rate Limit</Label><Input value={pkgForm.rate_limit} onChange={(e) => setPkgForm({ ...pkgForm, rate_limit: e.target.value })} placeholder="cth: 1M/1M 2M/2M 1536k/1536k 8/8 8 1M/1M" data-testid="hpf-rate" /></div>
                <div className="space-y-1.5"><Label>Validity</Label><Input value={pkgForm.validity} onChange={(e) => setPkgForm({ ...pkgForm, validity: e.target.value })} placeholder="cth: 2h / 1d / 30d" data-testid="hpf-validity" /></div>
                <div className="space-y-1.5"><Label>Time Limit</Label><Input value={pkgForm.time_limit} onChange={(e) => setPkgForm({ ...pkgForm, time_limit: e.target.value })} placeholder="cth: 5h (kosongkan jika -)" data-testid="hpf-timelimit" /></div>
                <div className="space-y-1.5"><Label>Data Limit</Label><Input value={pkgForm.data_limit} onChange={(e) => setPkgForm({ ...pkgForm, data_limit: e.target.value })} placeholder="cth: 500MB / 1GB (kosongkan = unlimited)" data-testid="hpf-datalimit" /></div>
                <div className="space-y-1.5"><Label>Login Type</Label><Input value="Username = Password" disabled data-testid="hpf-logintype" /></div>
                <div className="space-y-1.5"><Label>Harga Modal (Rp)</Label><Input type="number" min="0" value={pkgForm.modal} onChange={(e) => setPkgForm({ ...pkgForm, modal: e.target.value })} data-testid="hpf-modal" /></div>
                <div className="space-y-1.5"><Label>Harga Jual (Rp)</Label><Input type="number" min="0" value={pkgForm.jual} onChange={(e) => setPkgForm({ ...pkgForm, jual: e.target.value })} data-testid="hpf-jual" /></div>
                <div className="space-y-1.5"><Label>Lock User</Label>
                  <Select value={pkgForm.lock_user} onValueChange={(v) => setPkgForm({ ...pkgForm, lock_user: v })}>
                    <SelectTrigger data-testid="hpf-lockuser"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Enable">Enable</SelectItem>
                      <SelectItem value="Disable">Disable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Share User</Label><Input type="number" min="0" value={pkgForm.share_user} onChange={(e) => setPkgForm({ ...pkgForm, share_user: e.target.value })} placeholder="cth: 4 (0 = tidak dibatasi)" data-testid="hpf-shareuser" /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label>Jumlah kode per lembar</Label>
                  <Input type="number" min="1" max="100" value={pkgForm.codes_per_set ?? 1} onChange={(e) => setPkgForm({ ...pkgForm, codes_per_set: e.target.value })} data-testid="hpf-codesperset" />
                  <p className="text-[11px] text-muted-foreground">{parseSeragam(pkgForm.name) ? `Nama profile ${pkgForm.name} otomatis ${parseSeragam(pkgForm.name).count} kode per lembar; isi angka lain (>1) untuk menimpanya.` : "Default 1. Isi 4 untuk paket 4 HP → tiap lembar otomatis 4 kode berurutan (abcd1–abcd4); Harga Modal/Jual = harga per lembar."}</p>
                  {(() => { const n = Math.max(1, Math.min(100, Number(pkgForm.codes_per_set) || 1)); return (
                    <div className="rounded-lg border border-border bg-accent/40 px-3 py-2 text-xs" data-testid="hpf-codes-preview">
                      <span className="text-muted-foreground">Contoh 1 lembar ({n} kode):</span>{" "}
                      {n === 1 ? <span className="font-mono font-semibold">kxwp</span> : Array.from({ length: Math.min(n, 8) }, (_, i) => <span key={i} className="font-mono font-semibold mr-1.5">kxwp{i + 1}</span>)}
                      {n > 8 && <span className="font-mono text-muted-foreground">… kxwp{n}</span>}
                      <span className="text-muted-foreground"> — Harga Modal Rp {Number(pkgForm.modal || 0).toLocaleString("id-ID")} per lembar{n > 1 ? ` (≈ Rp ${Math.floor(Number(pkgForm.modal || 0) / n).toLocaleString("id-ID")}/kode)` : ""}</span>
                    </div>); })()}
                </div>
                <div className="col-span-2 flex items-center gap-2.5 border border-border rounded-lg px-3 py-2.5">
                  <input type="checkbox" id="hpf-showprice" className="accent-emerald-600 w-4 h-4" checked={pkgForm.show_price !== false} onChange={(e) => setPkgForm({ ...pkgForm, show_price: e.target.checked })} data-testid="hpf-showprice" />
                  <Label htmlFor="hpf-showprice" className="cursor-pointer text-sm font-normal">Tampilkan harga jual di voucher cetak (Thermal, Desktop, PDF, Gambar)</Label>
                </div>
                <div className="col-span-2 flex items-center gap-2.5 border border-amber-500/30 bg-amber-500/5 rounded-lg px-3 py-2.5">
                  <input type="checkbox" id="hpf-hideagents" className="accent-amber-500 w-4 h-4" checked={!!pkgForm.hide_from_agents} onChange={(e) => setPkgForm({ ...pkgForm, hide_from_agents: e.target.checked })} data-testid="hpf-hideagents" />
                  <Label htmlFor="hpf-hideagents" className="cursor-pointer text-sm font-normal">Sembunyikan profile dari semua level agen (hanya Admin yang bisa melihat & mencetak)</Label>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setPkgForm(null)} data-testid="btn-cancel-profile">Batal</Button>
                <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => { setPkgDelete(null); setPkgPinOpen(true); }} disabled={!pkgForm.name.trim() || savingPkg} data-testid="btn-save-profile">
                  {savingPkg ? "Menyimpan..." : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PinConfirm open={pkgPinOpen} onOpenChange={setPkgPinOpen}
        title={pkgDelete ? `Hapus Profile ${pkgDelete}?` : "Konfirmasi Simpan Profile"}
        description={pkgDelete ? "Profile dihapus dari router MikroTik & daftar harga." : "Masukkan PIN untuk menyimpan profile & harga voucher."}
        confirmLabel={pkgDelete ? "Hapus" : "Simpan"}
        onConfirm={pkgDelete ? doDeleteProfile : saveProfile} testid="pin-packages" />
      <PinConfirm open={pkgBulkOpen} onOpenChange={setPkgBulkOpen}
        title={`Hapus ${pkgSel.length} Profile Terpilih?`}
        description={`${pkgSel.slice(0, 8).join(", ")}${pkgSel.length > 8 ? `, +${pkgSel.length - 8} lainnya` : ""} — dihapus dari router MikroTik & daftar harga.`}
        confirmLabel="Hapus Semua" onConfirm={doBulkDeleteProfiles} testid="pin-pkg-bulk" />

      <VoucherPrint open={!!printData} onClose={() => setPrintData(null)} mode={printData?.mode || "thermal"} showPrice={printData?.showPrice !== false}
        vouchers={printData?.vouchers || []} profile={printData?.profile} price={printData?.price}
        routerName={printData?.routerName} createdAt={printData?.createdAt} />
      <Dialog open={!!priceAllDlg} onOpenChange={(o) => !o && setPriceAllDlg(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-price-all">
          <DialogHeader><DialogTitle>{priceAllDlg?.show ? "Tampilkan" : "Jangan Tampilkan"} Harga Jual — Semua Cabang</DialogTitle></DialogHeader>
          {priceAllDlg && (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-muted-foreground">Hanya berlaku untuk <b>WiFi Voucheran (hotspot)</b>: voucher cetak Thermal/Desktop/PDF/Gambar & VoucherChat. Harga jual <b>WiFi Rumahan (PPPoE)</b> tidak terpengaruh dan tetap tampil.</p>
              {!priceAllDlg.show && (
                <div className="space-y-1.5">
                  <Label>Kecualikan cabang (tetap tampilkan harga)</Label>
                  <Input placeholder="Cari cabang…" value={priceAllDlg.q} onChange={(e) => setPriceAllDlg({ ...priceAllDlg, q: e.target.value })} data-testid="price-all-search" />
                  <div className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border">
                    {routers.filter((r) => r.name.toLowerCase().includes(priceAllDlg.q.toLowerCase())).map((r) => (
                      <label key={r.id} className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-accent/50">
                        <Checkbox checked={priceAllDlg.exclude.has(r.id)} onCheckedChange={() => { const n = new Set(priceAllDlg.exclude); n.has(r.id) ? n.delete(r.id) : n.add(r.id); setPriceAllDlg({ ...priceAllDlg, exclude: n }); }} data-testid={`price-all-exclude-${r.name}`} />
                        <span className="truncate">{r.name}</span>
                      </label>
                    ))}
                  </div>
                  <div className="text-[11px] text-muted-foreground" data-testid="price-all-exclude-count">{priceAllDlg.exclude.size} cabang dikecualikan</div>
                </div>
              )}
              <Button className={`w-full ${priceAllDlg.show ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-600 hover:bg-rose-700"} text-white`} disabled={hidingAll} onClick={submitShowPriceAll} data-testid="price-all-submit">
                {hidingAll ? "Memproses…" : priceAllDlg.show ? "Tampilkan di Semua Cabang" : `Sembunyikan di ${routers.length - priceAllDlg.exclude.size} Cabang`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
